import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';

/**
 * Componente para processar o callback de autorização da Ludopedia
 * Este componente é renderizado na rota /auth/ludopedia/callback
 */
const LudopediaCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('Processando autenticação da Ludopedia...');
  const [hasProcessed, setHasProcessed] = useState(false);

  const processLudopediaAuth = useCallback(async (authCode: string, userId: string) => {
    setHasProcessed(true);
    setMessage('Verificando código de autorização e trocando por token de acesso...');
    try {
      // Obter a redirect_uri atual do frontend para garantir consistência
      const currentRedirectUri = window.location.origin + window.location.pathname;
      
      console.log('[LudopediaCallback] Chamando Supabase function com:', { authCode, userId, redirectUriFromClient: currentRedirectUri });

      const { data, error: functionError } = await supabase.functions.invoke(
        'ludopedia-exchange-code',
        { body: { authCode, userId, redirectUriFromClient: currentRedirectUri } }
      );

      if (functionError) {
        console.error('Erro ao chamar a Supabase Function:', functionError);
        // Tentar extrair mais detalhes do erro
        let detailedMessage = 'Falha ao comunicar com o servidor para autenticação Ludopedia.';
        if (functionError.context && typeof functionError.context.json === 'function') {
          try {
            const errorDetails = await functionError.context.json();
            console.error('Detalhes do erro da Supabase Function:', errorDetails);
            detailedMessage = errorDetails.error || errorDetails.message || detailedMessage;
            if (errorDetails.details) {
              detailedMessage += ` (Detalhes: ${JSON.stringify(errorDetails.details)})`;
            }
          } catch (parseError) {
            console.warn('Não foi possível fazer parse do JSON de erro da função:', parseError);
            // Se não conseguir fazer parse, usar a mensagem original do functionError
            detailedMessage = functionError.message || detailedMessage;
          }
        } else if (functionError.message) {
            detailedMessage = functionError.message;
        }
        throw new Error(detailedMessage);
      }

      if (data && data.error) {
        console.error('Erro retornado pela Supabase Function (no objeto data):', data.error, data.details);
        throw new Error(data.message || data.error || 'Falha no processo de autenticação com a Ludopedia.');
      }
      
      if (data) {
        toast.success(data.message || 'Conta da Ludopedia conectada com sucesso!');
        setMessage('Conexão bem-sucedida! Redirecionando...');
        
        // Atualizar o perfil do usuário no AuthContext
        if (data.ludopedia_username) {
          try {
            await updateProfile({ 
              ludopedia_username: data.ludopedia_username, 
              // O ludopedia_user_id e ludopedia_access_token já foram salvos no banco pela function,
              // o AuthContext irá buscá-los na próxima vez que o perfil for carregado.
              // Apenas o username é suficiente para o updateProfile indicativo aqui.
            });
            console.log('[LudopediaCallback] Perfil do AuthContext atualizado (ou tentativa) com ludopedia_username.');
          } catch (profileUpdateError) {
            console.error('[LudopediaCallback] Erro ao tentar atualizar o perfil no AuthContext:', profileUpdateError);
            toast.error('Houve um problema ao atualizar seu perfil localmente. Por favor, recarregue a página.');
          }
        }
        
        window.location.assign('/integrations?ludopedia_connected=true');
      } else {
        throw new Error('Resposta inesperada do servidor durante a autenticação Ludopedia.');
      }

    } catch (e: any) {
      console.error('Erro no processLudopediaAuth:', e);
      setError(e.message || 'Ocorreu um erro desconhecido ao conectar com a Ludopedia.');
      toast.error(e.message || 'Erro ao conectar com a Ludopedia.');
      setLoading(false);
    }
  }, [navigate, updateProfile]);

  useEffect(() => {
    if (hasProcessed) return;

    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(`Erro da Ludopedia: ${errorDescription || errorParam}`);
      toast.error(`Erro da Ludopedia: ${errorDescription || errorParam}`);
      setLoading(false);
      return;
    }

    if (!code) {
      setError('Nenhum código de autorização da Ludopedia encontrado na URL.');
      toast.error('Código de autorização não encontrado.');
      setLoading(false);
      return;
    }

    if (!user) {
      setMessage("Aguardando informações do usuário...");
      return;
    }
    
    processLudopediaAuth(code, user.id);

  }, [searchParams, user, processLudopediaAuth, navigate, hasProcessed]);

  if (!user && loading && !error && !hasProcessed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-700">Aguardando informações do usuário...</h2>
        <p className="text-gray-500">Por favor, aguarde.</p>
      </div>
    );
  }

  if (loading && !error && !hasProcessed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-700">{message}</h2>
        <p className="text-gray-500">Por favor, aguarde...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">Erro na Conexão Ludopedia!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
        <Button onClick={() => navigate('/integrations', { replace: true })} variant="outline">
          Voltar para Integrações
        </Button>
      </div>
    );
  }

  if (hasProcessed && !error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
         <h2 className="text-xl font-semibold text-gray-700">Conexão bem-sucedida! Redirecionando...</h2>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
       <h2 className="text-xl font-semibold text-green-700">Redirecionando...</h2>
    </div>
  );
};

export default LudopediaCallback; 